import React, { useCallback, useEffect, useRef, useState, useMemo, useImperativeHandle, forwardRef } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import mapboxgl from "mapbox-gl";
import Settings from "./Components/Settings/Settings";
import "./types/sceneTypes"; // Import types for JSDoc
import {
  APARTMENT,
  ARROWS_DISTANCE,
  ARROWS_ROTATION,
  DEFAULT,
  MAPBOX_TOKEN,
  ORBIT,
  ROTATE_FLOORS,
  ROTATE_MODEL,
  STEADY,
  TRANSPARENCY,
  X,
} from "./constants";

import SkyController from "./Components/Enviroment/SkyController";
import { SCENE_CONFIG, DEFAULT_TRANSPARENCY, HOVERED_TRANSPARENCY } from "./config/sceneConfig";
import { COLOR_CONFIG, createGradientColors, FLOOR_MATERIAL_CONFIG } from "./config/colorConfig";
import {
  getFloorNumFromName,
  getHighestPointFromIntersections,
  getAllMeshesFromIntersections,
  getLinePositions,
  calculatePolarAngle,
  getDirection,
  getNextPosition,
  updateLine,
  updateLabelPosition,
  getNodesByName,
  getNearestObjectBelowCamera,
  getCameraPositionFromLabel,
  createBuildingPositionsArray,
} from "./utils/sceneUtils";


import {
  animateCamera,
  animateCameraPosition,
  calculateDestinationPoint,
  createArrowAndLabel,
  getCurrentViewData,
  getModelSize,
  handleCircleSize,
} from "./utils/threeJsUtils";
import { cameraRenderDistance } from "./Components/Enviroment";
import CameraController, {
  initialCameraHeight,
  maxPolarAngle,
  minPolarAngle,
} from "./Components/CameraController";
import { loadModelGLTF } from "./utils/loadingManager";
import Grid from "./Components/Grid";
import useKey from "./hooks/useKey";
import { useSceneMaterials } from "./hooks/useSceneMaterials";
import { useSceneRefs } from "./hooks/useSceneRefs";
import { useSceneState } from "./hooks/useSceneState";
import { useCameraControls } from "./hooks/useCameraControls";
import { useApartmentNodes } from "./hooks/useApartmentNodes";
import { useModelsRefs } from "./hooks/useModelsRefs";

import Presets from "./Components/Presets";
import ApartmentSettings from "./Components/Settings/ApartmentSettings";
import EnvironmentManager from "./Components/EnvironmentManager";
import effectController from "./Components/Enviroment/effectsController";
import TouredApartment from "./Components/TouredApartment";
import useMovement from "./hooks/useMovement";
import VideoScreen from "./Components/VideoScreen";
import SpotLight from "./Components/SpotLight";
import MyPointLight from "./Components/MyPointLight.";
import TexturesManager from "./Components/TexturesManager/TexturesManager";

import ModelsManager from "./Components/Models/ModelsManager";
import LightsManager from "./Components/LightsManager/LightsManager";
import { initialLineWidth } from "./Components/Settings/defaultSettings";
import MovementControls from "./Components/MovementControls";
import MobileMovementController from "./Components/MobileMovementController";
import Points from "./Components/Settings/Points";
import TileSet from "./Components/TileSet";
import PositionsSlider from "./Components/PositionsSlider";
import Compass from "./Components/Compass";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";


// import { OGC3DTile } from '@jdultra/threedtiles';

export const modes = {
  [DEFAULT]: {
    cameraMode: ORBIT,
  },
  [APARTMENT]: {
    cameraMode: STEADY,
  },
};
mapboxgl.accessToken = MAPBOX_TOKEN
const Scene = forwardRef(({
  setMousePosition,
  viewedApartment,
  touredApartment,
  saveApartmentViewData,
  saveModelCalibration,
  saveProjectSettings,
  hoveredApartment,
  projectImages = [],
  buildings,
  floors,
  floor,
  tileSetUrl,
  // modelUrl,
  projectModels,
  loadedModels,
  show3dMap,
  isRtl,
  setModelReady,
  selectedBuildingName,
  apartments,
  onApartmentClick,
  onApartmentHover,
  colors,
  projectDefaultView,
  saveStatus,
  isAdmin,
  onAnimationEnd,
  cameraPosition,
  onBackButtonClick,
  onBuildingSelect
}, ref) => {
  // Use custom hooks for state management
  const {
    initialized,
    setInitialized,
    animationEnded,
    setAnimationEnded,
    modelsLoaded,
    setModelsLoaded,
    environmentPreset,
    setEnvironmentPreset,
    selectedFloor,
    setSelectedFloor,
    showGrid,
    setShowGrid,
    selectedApartmentNode,
    setSelectedApartmentNode,
    showPresets,
    setShowPresets,
    selectedModelId,
    setSelectedModelId,
    showEnvironment,
    setShowEnvironment,
    focusedBuilding,
    setFocusedBuilding,
  } = useSceneState(projectDefaultView.showInitialAnimation);
  const apartmentNodes = loadedModels.apartmentNodes;
  useImperativeHandle(ref, () => ({
    takeSnapshot: () => {
      if (renderer.current && scene.current && camera.current) {
        renderer.current.render(scene.current, camera.current);
        return renderer.current.domElement.toDataURL("image/png");
      }
      return null;
    },
  }));
  // Use custom hooks for materials
  const {
    vacantColor,
    occupiedColor,
    vacantHoveredColor,
    occupiedHoveredColor,
    floorMat,
  } = useSceneMaterials(colors.primary, projectDefaultView.transparency);

  // State for positions slider
  const [buildingPositions, setBuildingPositions] = useState([]);

  let targetPosition;
  let arrowsRefs = useRef([]);
  let arrowsGroup = useRef(new THREE.Group());
  const mountRef = useRef(null);
  const currentMode = useRef(DEFAULT);
  const movementRef = useRef({});
  let raycaster;
  let mouse;
  let pmremGenerator;
  let flyControls;

  movementRef.current = useMovement({ currentMode: currentMode.current });

  const gridSize =
    projectDefaultView && projectDefaultView.maxDistance
      ? projectDefaultView.maxDistance * 3
      : 600; // This defines the size of the grid, adjust as needed
  const appRef = useRef();
  const mainCircle = useRef();
  const controls = useRef();
  const renderer = useRef();
  const scene = useRef();
  const camera = useRef();

  const appTypeModelsRef = useRef({});
  const tileSetRef = useRef();
  const mousePosition = useRef();


  const movedFloorNodes = useRef([]);
  const hiddenNodes = useRef([]);


  const floorLinesWidth = useRef(initialLineWidth);
  const clockRef = useRef();
  const floorLineStartXOffset = useRef(0);
  const podiumRef = useRef();
  const grid = useRef();
  const lookAtPosition = useRef();
  const spritesNodesRef = useRef([]);
  const areaNodesRef = useRef([]);

  const humanGroupRef = useRef(new THREE.Group());

  let mouseDown = false;

  // Initialize camera controls hook
  const cameraControls = useCameraControls({
    camera,
    controls,
    projectDefaultView,
  });
  const { buildBuildingGroups, buildProjectModels, modelsRef, toggleModelsVisibility, resetBuildingsTransform, floorsNodes, floorLines, floorLabels } = useModelsRefs({
    appTypeModelsRef,
    scene,
    spritesNodesRef,
    areaNodesRef,
    loadedModels,
    buildings,
    projectModels,
    projectDefaultView,
    gradientColors: createGradientColors(colors.primary),
    vacantColor,
    occupiedColor,
    occupiedHoveredColor,
    floorMat,
    setModelReady,
    colors,
  });
  // Initialize apartment nodes hook
  const apartmentManager = useApartmentNodes({
    apartmentNodes,
    vacantColor,
    occupiedColor,
    vacantHoveredColor,
    occupiedHoveredColor,
    appRef,
  });

  // Initialize models refs hook

  console.log("appTypeModelsRef.current", appTypeModelsRef.current);
  let initialArrowsRadius = SCENE_CONFIG.INITIAL_ARROWS_RADIUS;

  useEffect(() => {
    targetPosition = new THREE.Vector3();

    !scene.current && init();
    // console.log("CREATING MAIN CIRCLE")

    // const direction = new THREE.Vector3(0, 0, -1); // Default direction
    // const length = 2; // Length of the arrow
    // const color = 0xffff00; // Color of the arrow
    // const arrowHelper = new THREE.ArrowHelper(
    //   direction,
    //   humanGroupRef.current.position,
    //   length,
    //   color
    // );
    // humanGroupRef.current.add(arrowHelper);


    return () => {
      floorLabels.current = [];
      floorLines.current = [];
      movedFloorNodes.current = [];
      floorsNodes.current = [];
      arrowsRefs.current = [];
    };
  }, []);

  useEffect(() => {
    setInputHandlers();
  }, [cameraControls.lastCameraViewData]);
  useEffect(() => {
    console.log("projectDefaultView", projectDefaultView);
    if (!projectDefaultView.showInitialAnimation) onAnimationEnd();

    if (projectDefaultView) {
      // projectDefaultView.podiumScale && changePodiumSize(projectDefaultView.podiumScale)
      floorLinesWidth.current = projectDefaultView.lineWidth
        ? +projectDefaultView.lineWidth
        : initialLineWidth;
      floorLineStartXOffset.current = projectDefaultView.lineStart
        ? +projectDefaultView.lineStart
        : 0;
      if (!projectDefaultView.showOcean) {
        setShowGrid(true);
      } else {
        setShowGrid(false);
      }
    }
    toggleAppTypesVisibility(false);
    return () => { };
  }, [projectDefaultView]);
  // useEffect(() => {
  //   if (cameraPosition) {
  //     console.log("cameraPosition", cameraPosition);
  //     cameraControls.disableControls();


  //     animateCamera(
  //       camera.current,
  //       cameraPosition.position,
  //       cameraPosition.quaternion,
  //       1
  //     );

  //   }
  // }, [cameraPosition]);

  useEffect(() => {
    // console.log({ hoveredApartment });
    if (hoveredApartment && currentMode.current === DEFAULT)
      apartmentManager.setAllApartmentsNodes([hoveredApartment.id]);
  }, [hoveredApartment]);

  useEffect(() => {
    // console.log("viewedApartment", viewedApartment);
    if (viewedApartment) {
      apartmentManager.setAllApartmentsNodes();
      cameraControls.enableControls();
      if (
        viewedApartment.default_view &&
        viewedApartment.default_view.position
      ) {
        targetPosition = viewedApartment.default_view.position;
        animateCameraPosition(camera.current, targetPosition, 1);
      }
    }
  }, [viewedApartment]);
  useEffect(() => {
    handleTouredApartment();
  }, [touredApartment]);
  const handleTouredApartment = async () => {
    console.log("[FloorView] handleTouredApartment", touredApartment);

    if (touredApartment) {
      onApartmentHover();
      apartmentManager.setAllApartmentsNodes();
      setShowGrid(false)

      const buildingRotation =
        touredApartment.type_building &&
        touredApartment.type_building.default_view &&
        touredApartment.type_building.default_view.rotation;
      if (!buildingRotation) {
        console.error(
          "[FloorView] Apartment's type_building is missing default_view.rotation — malformed building data, camera rotation will fall back to 0",
          { apartmentId: touredApartment.id, type_building: touredApartment.type_building }
        );
      }
      if (touredApartment.floor_id === undefined || touredApartment.floor_id === null) {
        console.error(
          "[FloorView] Apartment is missing floor_id — malformed apartment data, floor lookup will fail",
          { apartmentId: touredApartment.id, apartmentTitle: touredApartment.title || touredApartment.name }
        );
      }
      const apartmentNode = apartmentNodes.find(
        (node) => node.userData.apartment.id == touredApartment.id
      );

      if (!apartmentNode) {
        console.error(
          "[FloorView] Apartment node not found in 3D model — the apartment's bbox node is missing or its name doesn't match the apartment title in the building GLB",
          {
            apartmentId: touredApartment.id,
            apartmentTitle: touredApartment.title || touredApartment.name,
            availableApartmentNodeIds: apartmentNodes.map((n) => n.userData.apartment && n.userData.apartment.id),
          }
        );
        return;
      }

      const appType = apartmentNode.userData.apartment.apartment_type;
      if (!appType) {
        console.error(
          "[FloorView] Apartment is missing apartment_type data — cannot resolve which model to show",
          { apartmentId: touredApartment.id, apartmentTitle: touredApartment.title || touredApartment.name }
        );
        return;
      }

      if (appTypeModelsRef.current[appType.name]) {
        if (currentMode.current === DEFAULT) cameraControls.setLastCameraViewData(getCurrentViewData(camera.current));
        cameraControls.disableControls();
        currentMode.current = APARTMENT

        const worldPosition = new THREE.Vector3();
        apartmentNode.getWorldPosition(worldPosition);
        // console.log("apartmentNode worldPosition", worldPosition);
        const typeNode = appTypeModelsRef.current[appType.name];
        // console.log("typeNode", typeNode);
        typeNode.visible = true;
        typeNode.position.set(worldPosition.x, worldPosition.y, worldPosition.z);
        typeNode.rotation.set(0, buildingRotation ? buildingRotation._y : 0, 0);
        typeNode.children.forEach((child) => {
          child.visible = true;
        });

        setSelectedApartmentNode(typeNode);
        setShowPresets(false);
        setTimeout(async () => {
          try {
            await moveToApartmentNode(typeNode);
            console.log("[FloorView] camera positioning succeeded", {
              apartmentId: touredApartment.id,
              apartmentType: appType.name,
            });
          } catch (error) {
            console.error(
              "[FloorView] camera positioning failed while moving to apartment node",
              { apartmentId: touredApartment.id, apartmentType: appType.name },
              error
            );
          }

          const floor = floors.find((f) => f.id === touredApartment.floor_id);
          if (!floor) {
            console.error(
              "[FloorView] Floor not found for apartment — no floor with this id in the project's floors list",
              {
                apartmentId: touredApartment.id,
                apartmentFloorId: touredApartment.floor_id,
                availableFloorIds: floors && floors.map((f) => f.id),
              }
            );
          } else {
            console.log("[FloorView] Floor resolved for apartment", { apartmentId: touredApartment.id, floor });
          }
          // toggleEntitiesVisibility("modelsRef", false);
          setSelectedFloor(floor);
        }, 300);

      } else {
        console.error(
          "[FloorView] Apartment type model (GLB) not registered — no clone was built for this apartment_type. This usually means the apartment's bbox node in the building GLB has no child named \"balcony\", or apartment_type.name doesn't match between apartments",
          {
            apartmentId: touredApartment.id,
            apartmentTypeName: appType.name,
            registeredApartmentTypes: Object.keys(appTypeModelsRef.current),
          }
        );
      }

    } else {
      appRef.current = null;
      setShowPresets(true);
      setSelectedFloor(null);

      toggleEntitiesVisibility("modelsRef", true);

      setSelectedApartmentNode(null);
      apartmentNodes.forEach((node) => {
        node.visible = true;
      });
      toggleAppTypesVisibility(false);
      currentMode.current = DEFAULT;


    }
  }


  useEffect(() => {
    handleSelectedBuildingName();
    return () => { };
  }, [selectedBuildingName, modelsRef.current, mainCircle.current]);
  useEffect(() => {
    handleFloorAnimation();

    return () => { };
  }, [floor]);
  useEffect(() => {
    handlePresetChange();
    toggleAppTypesVisibility(false)
  }, [environmentPreset]);


  useEffect(() => {
    // console.log("projectModels useEffect", projectModels);
    if (projectModels) {
      buildProjectModels();
      buildBuildingGroups();
      apartmentManager.setAllApartmentsNodes([]);
      toggleAppTypesVisibility(false)
    }

  }, [projectModels]);

  // Effect to populate building positions for the slider
  useEffect(() => {
    if (buildings && buildings.length > 0 && scene.current && modelsRef.current && Object.keys(modelsRef.current).length > 0) {
      const positions = createBuildingPositionsArray(
        buildings,
        scene.current,
        modelsRef.current,
        getModelSize
      );
      
      if (positions.length > 0) {
        setBuildingPositions(positions);
        // console.log(`Loaded ${positions.length} building positions for slider`);
      }
      toggleAppTypesVisibility(false);
    }
  }, [buildings, modelsRef.current, scene.current]);



  const handlePresetChange = () => {



    toggleEntitiesVisibility("arrows", environmentPreset !== "Floors");

  };


  const toggleEntitiesVisibility = (entitiyName, visible) => {
    switch (entitiyName) {
      case "modelsRef":
        Object.keys(modelsRef.current).forEach((key) => {
          modelsRef.current[key].visible = visible;
        });

        break;
      case "arrows":
        if (arrowsGroup.current)
          arrowsGroup.current.visible = visible;
      // arrowsRefs.current.forEach((arrow) => {
      //   arrow.visible = visible;
      // });
      default:
        break;
    }
  };
  const toggleAppTypesVisibility = (visible) => {
    Object.keys(appTypeModelsRef.current).forEach((key) => {
      appTypeModelsRef.current[key].position.y = visible ? 0 : 1000;
      appTypeModelsRef.current[key].visible = visible;
    });
  };

  function init() {
    clockRef.current = new THREE.Clock();
    mouse = new THREE.Vector2();
    scene.current = new THREE.Scene();
    raycaster = new THREE.Raycaster();
    createRenderer();
    pmremGenerator = new THREE.PMREMGenerator(renderer.current);
    pmremGenerator.compileEquirectangularShader();
    setCamera();

    loadDirectionArrows();

    // scene.current.add(podiumRef.current);


    // humanGroupRef.current.add(createUserMesh());
    window.addEventListener("resize", function () {
      var width = window.innerWidth;
      var height = window.innerHeight;
      renderer.current.setSize(width, height);
      camera.current.aspect = width / height;
      camera.current.updateProjectionMatrix();
    });

    const applyMoveAndRotate = (direction) => {
      // console.log(movementRef.current.moveLeft)
      const {
        moveForward,
        moveBackward,
        moveLeft,
        moveRight,
        moveUp,
        moveDown,
        lookLeft,
        lookRight,
        lookUp,
        lookDown,
      } = movementRef.current;
      if (moveUp) {
        moveCamera("up");
      }
      if (moveDown) {
        moveCamera("down");
      }
      if (moveLeft) {
        moveCamera("left");
      }
      if (moveRight) {
        moveCamera("right");
      }
      if (moveForward) {
        moveCamera("forward");
      }
      if (moveBackward) {
        moveCamera("backward");
      }
      if (lookLeft) {
        rotateCamera("left");
      }
      if (lookRight) {
        rotateCamera("right");
      }
      if (lookUp) {
        rotateCamera("up");
      }
      if (lookDown) {
        rotateCamera("down");
      }
    };
    function animate() {

      requestAnimationFrame(animate);

      applyMoveAndRotate();

      humanGroupRef.current.rotation.copy(camera.current.rotation);
      humanGroupRef.current.position.set(
        camera.current.position.x,
        0,
        camera.current.position.z
      );

      controls.current && controls.current.update(); // Only required if controls.enableDamping = true, or if controls.autoRotate = true
      camera.current.updateMatrixWorld();
      // console.log(camera.position)
      // if (tilesRenderer.current && camera.current && renderer.current)
      // tilesRenderer.current.update();
      // camera.current.lookAt(tilesRenderer.current.group.position);
      renderer.current.render(scene.current, camera.current);
    }

    if (renderer.current && camera.current) {
      animate();
    }
    setInitialized(true);
  }



  async function handleFloorAnimation() {
    let targetPosition;
    console.log("[FloorView] handleFloorAnimation", floor);
    if (floor) {
      if (!floor.building || floor.name === undefined || floor.name === null || Number.isNaN(+floor.name)) {
        console.error(
          "[FloorView] handleFloorAnimation: floor object is set but malformed — missing building or invalid/non-numeric floor name, lookups below will not match",
          { floor }
        );
      }
      const floorsByBuilding = floorsNodes.current.filter(
        (node) => node.userData.buildingName == floor.building
      );
      console.log("[FloorView] floorsByBuilding", floorsByBuilding);
      const floorNode = floorsByBuilding.find(
        (node) => +getFloorNumFromName(node.name) == floor.name
      );
      if (!floorNode) {
        console.error(
          "[FloorView] Floor node not found in the 3D model for requested floor — check floor naming in the building GLB",
          {
            requestedBuilding: floor.building,
            requestedFloorName: floor.name,
            availableFloorNodeNames: floorsByBuilding.map((n) => n.name),
            allBuildingNamesInFloorsNodes: [...new Set(floorsNodes.current.map((n) => n.userData.buildingName))],
          }
        );
      }
      if (floorNode) {

        const floorsAbove = floorsByBuilding.filter(
          (node) =>
            +getFloorNumFromName(node.name) > +floor.name
        );
        const sortedByFloorNum = floorsAbove.sort(
          (a, b) => +getFloorNumFromName(a.name) - +getFloorNumFromName(b.name)
        );
        // console.log(floorsAbove)
        movedFloorNodes.current = sortedByFloorNum;
        // console.log("movedFloorNodes.current", movedFloorNodes.current);
        gsap.utils
          .toArray(movedFloorNodes.current.reverse())
          .forEach((child, i) => {
            gsap.to(child.position, {
              y: child.position.y + 1000,
              duration: 1,
              ease: "power2.out",
              delay: i * 0.05, // Add delay here for stagger effect
            });
          });
        const model = modelsRef.current[floor.building];
        const building = buildings.find((b) => b.name == floor.building);
        if (!building) {
          console.error(
            "[FloorView] Building not found for floor — camera positioning aborted (floor.building doesn't match any building name)",
            { requestedBuilding: floor.building, availableBuildingNames: buildings.map((b) => b.name) }
          );
          return;
        }
        const movedFloorsNames = movedFloorNodes.current.map(
          (node) => node.name
        );
        model.traverse(function (node) {
          if (
            node.isMesh &&
            node.position.y > floorNode.position.y &&
            !movedFloorsNames.includes(node.name)
          ) {
            node.visible = false;
            hiddenNodes.current.push(node);
          }
        });
        if (building.default_view && building.default_view.floorsView) {
          targetPosition = new THREE.Vector3(
            building.default_view.floorsView.position.x,
            (floorNode.position.y + 50) * building.default_view.scale.y,
            building.default_view.floorsView.position.z
          );
          const lookAt = new THREE.Vector3(
            targetPosition.x,
            targetPosition.y - 50,
            targetPosition.z
          );
          floorLines.current.forEach((line) => (line.visible = false));
          floorLabels.current.forEach((label) => (label.visible = false));
          cameraControls.animateLookAtPosition(lookAt, 4);
          try {
            await animateCameraPosition(camera.current, targetPosition, 3);
            console.log("[FloorView] handleFloorAnimation: camera positioning succeeded", {
              building: floor.building,
              floorName: floor.name,
            });
          } catch (error) {
            console.error(
              "[FloorView] handleFloorAnimation: camera positioning failed while animating to floor view",
              { building: floor.building, floorName: floor.name },
              error
            );
          }
        } else {
          console.error(
            "[FloorView] Floor node found, but building has no default_view.floorsView configured — camera positioning skipped",
            { building: floor.building, floorName: floor.name, buildingDefaultView: building.default_view }
          );
        }
      }
    } else {
      if (movedFloorNodes.current && movedFloorNodes.current.length) {
        console.log("movedFloorNodes.current", movedFloorNodes.current);
        gsap.utils
          .toArray(movedFloorNodes.current.reverse())
          .forEach((child, i) => {
            gsap.to(child.position, {
              y: child.position.y - 1000,
              duration: 1,
              ease: "power2.out",
              delay: i * 0.05, // Add delay here for stagger effect
              onComplete: () => {
                movedFloorNodes.current = [];
              },
            });
          });

        floorLines.current.forEach((line) => (line.visible = true));
        floorLabels.current.forEach((label) => (label.visible = true));
        hiddenNodes.current.forEach((node) => (node.visible = true));
      }
      moveToDefaultView();
    }
  }


  const handleSelectedBuildingName = () => {
    console.log("handleSelectedBuildingName", selectedModelId, modelsRef.current);
    if (!selectedModelId || !modelsRef.current) return; {
      // if (mainCircle.current) {
      const selectedBuilding = buildings.find(
        (b) => b.id == selectedModelId
      );
      buildings.forEach((building) => {
        if (building.name == selectedModelId) {
          modelsRef.current[building.id].visible = true;
        } else {
          modelsRef.current[building.id].visible = false;
        }
      });
      if (selectedBuilding) {

        if (
          selectedBuilding &&
          selectedBuilding.default_view &&
          selectedBuilding.default_view.camera
        ) {
          targetPosition = selectedBuilding.default_view.camera.position;
          // const lookAt = calculateDestinationPoint(
          //   {
          //     position: selectedBuilding.default_view.camera.position,
          //     quaternion: selectedBuilding.default_view.camera.quaternion,
          //   },
          //   100
          // );
          // lookAtPosition.current = lookAt;
          animateCamera(
            camera.current,
            selectedBuilding.default_view.camera.position,
            selectedBuilding.default_view.camera.quaternion,
            1
          );


        }

        // handleSelectedBuildingForEastProjectOnly(selectedBuildingName);
      }
    }
    //  console.log(mainCircle.current)
  };


  const loadDirectionArrows = async () => {
    // Create gradient
    let canvas = document.createElement("canvas");
    canvas.width = 2;
    canvas.height = 2;

    let context = canvas.getContext("2d");
    context.fillStyle = colors.primary;
    context.fillRect(0, 0, 1, 2);

    // context.fillStyle = "black";
    context.fillRect(1, 0, 1, 2);

    // Create texture
    let texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;

    const height = 5;
    const arrow = await loadModelGLTF(require("./arrow_3.glb"));
    arrow.traverse(function (node) {
      if (node.isMesh) {
        node.material.map = texture;
        node.material.needsUpdate = true;
      }
    });
    arrow.scale.set(50, 50, 10); // Scale the model
    arrow.rotation.x = Math.PI / 2; // Rotate 90 degrees around the x-axis
    arrow.position.y = height;
    let radius = initialArrowsRadius;
    if (projectDefaultView) {
      if (projectDefaultView.arrowsRadiusOffset)
        radius += +projectDefaultView.arrowsRadiusOffset;
      if (projectDefaultView.arrowsRotation)
        arrowsGroup.current.rotation.y = +projectDefaultView.arrowsRotation / 10;
    }
    const arrowsData = [
      {
        labelName: "S",
        position: [-radius, arrow.position.y, arrow.position.z],
        rotation: [arrow.rotation.x, arrow.rotation.y, Math.PI],
        labelPosition: [-radius, height + 4, 0],
      },
      {
        labelName: "W",
        position: [arrow.position.x, arrow.position.y, -radius],
        rotation: [arrow.rotation.x, arrow.rotation.y, -Math.PI / 2],
        labelPosition: [0, height + 4, -radius],
      },
      {
        labelName: "E",
        position: [arrow.position.x, arrow.position.y, radius],
        rotation: [arrow.rotation.x, arrow.rotation.y, Math.PI / 2],
        labelPosition: [0, height + 4, radius],
      },
      {
        labelName: "N",
        position: [radius, arrow.position.y, arrow.position.z],
        rotation: [arrow.rotation.x, arrow.rotation.y, arrow.rotation.z],
        labelPosition: [radius, height + 4, 0],
      },
    ];
    arrowsData.forEach((arrowData) => {
      const arrowGroup = createArrowAndLabel(
        arrow,
        arrowData.position,
        arrowData.rotation,
        arrowData.labelName,
        arrowData.labelPosition
      );
      // arrowsRefs.current.push(arrowGroup);
      arrowsGroup.current.add(arrowGroup);

    });

    scene.current.add(arrowsGroup.current);
  };


  const createRenderer = () => {
    renderer.current = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.current.setSize(window.innerWidth, window.innerHeight);
    renderer.current.shadowMap.enabled = true; // Enable shadow maps
    // renderer.current.shadowMap.type = THREE.PCFSoftShadowMap;
    const mapDiv = document.getElementById("map");
    renderer.current.domElement.addEventListener(
      "webglcontextrestored",
      function () { },
      false
    );
    mapDiv.prepend(renderer.current.domElement);
  };


  const setCamera = () => {
    camera.current = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      cameraRenderDistance * 2
    );
  };





  const rotateCamera = (direction) => {
    const speed = 1;
    const currentDirection = camera.current.getWorldDirection(
      new THREE.Vector3()
    );
    lookAtPosition.current = calculateDestinationPoint(
      {
        position: camera.current.position,
        quaternion: camera.current.quaternion,
      },
      100
    );

    switch (direction) {
      case "left":
        const leftDirection = getDirection(currentDirection, "left");
        lookAtPosition.current.x += leftDirection.x;
        lookAtPosition.current.z += leftDirection.z;
        break;
      case "right":
        const rightDirection = getDirection(currentDirection, "right");
        lookAtPosition.current.x += rightDirection.x;
        lookAtPosition.current.z += rightDirection.z;
        break;
      case "up":
        lookAtPosition.current.y += speed;
        break;
      case "down":
        lookAtPosition.current.y -= speed;
        break;
      default:
        break;
    }
    cameraControls.setControlsTarget(lookAtPosition.current);

  };

  const moveCamera = (direction) => {
    // console.log("moveCamera", direction);
    let speed = 1;
    if (currentMode.current === APARTMENT) speed = 0.13;
    const currentDirection = camera.current.getWorldDirection(
      new THREE.Vector3()
    );
    const currentPosition = camera.current.position.clone();
    let nextPosition = getNextPosition(
      direction,
      currentPosition,
      currentDirection,
      speed
    );

    const raycaster = new THREE.Raycaster();
    const moveVector = nextPosition.clone().sub(currentPosition).normalize();
    // if (boundaryNode.current) {
    //   const boundingBox = new THREE.Box3().setFromObject(boundaryNode.current);
    //   // Check X and Z bounds only
    //   console.log("Bounding Box:", boundingBox);
    //   console.log("nextPosition:", nextPosition);
    //   if (
    //     nextPosition.x < boundingBox.min.x ||
    //     nextPosition.x > boundingBox.max.x ||
    //     nextPosition.z < boundingBox.min.z ||
    //     nextPosition.z > boundingBox.max.z
    //   ) {
    //     console.log("Out of bounds, stopping camera movement");
    //     return;
    //   }
    // }
    // Set the raycaster to detect collisions
    raycaster.set(currentPosition, moveVector);
    raycaster.camera = camera.current; // Set the camera for raycasting against sprites
    const intersects = raycaster.intersectObjects(scene.current.children, true);

    camera.current.position.copy(nextPosition);
    if (intersects.length > 0 && intersects[0].distance < 0.3) {
      // Collision detected, do not move the camera
      console.log("Collision detected, stopping camera movement");
      return
    } else {
      // No collision, move the camera
      // 
      camera.current.position.copy(nextPosition);
      // nextPosition = getNextPosition(
      //   direction,
      //   humanGroupRef.current.position,
      //   currentDirection,
      //   speed
      // );
      // humanGroupRef.current.position.copy(nextPosition);
    }
  };


  async function onPointClick(point) {
    console.log("onPointClick", point);
    if (point) {
      // Move the camera to the clicked point
      currentMode.current = APARTMENT;

      let strightForwardLoogAtPosition = calculateDestinationPoint(
        {
          position: point,
          quaternion: camera.current.quaternion,
        },
        100
      );
      if (!cameraControls.lastCameraViewData) cameraControls.setLastCameraViewData(getCurrentViewData(camera.current));
      const straightForwardQuaterion = new THREE.Quaternion();
      strightForwardLoogAtPosition.y = point.y
      cameraControls.animateLookAtPosition(strightForwardLoogAtPosition, 2);
      await animateCamera(camera.current, point, straightForwardQuaterion, 2);
    }
  }

  const getAllLabelsInScene = () => {
    const labels = [];
    scene.current.traverse((child) => {
      if (child.isSprite) {
        labels.push(child);
      }
    });
    return labels;
  };

  // Separate function to handle sprite clicks
  async function handleSpriteClick(sprite) {
    focuseToBuildingByName(sprite.name); 
    const building = buildings.find((b) => b.name === sprite.name);
    if (building && onBuildingSelect) {
      onBuildingSelect(building.id);
    }

  }
  async function focuseToBuildingByName(buildingName) {
    // console.log("focuseToBuildingByName", buildingName);
    cameraControls.enableControls();
    currentMode.current = DEFAULT
    setFocusedBuilding(buildingName);
    // Get camera position and look-at position from label
    const positions = getCameraPositionFromLabel(
      buildingName,
      scene.current,
      modelsRef.current,
      getModelSize
    );
    
    if (!positions) return;
    
    const { cameraPosition, lookAtPosition } = positions;
    const labelNode = getNodesByName(scene.current, buildingName)[0];
    const buildingId = labelNode.userData.id;
    const model = modelsRef.current[buildingId];
    if (!model) return;
    console.log("model before reset", model.position);
    // model.position.y = 0;
    // console.log("labelNode", labelNode);
    // console.log("buildingModel", modelsRef.current[buildingId]);
    
    currentMode.current = DEFAULT;
    toggleModelsVisibility([buildingId]);
    setShowGrid(true);
    setShowEnvironment(false);
    setSelectedFloor(null);

    if (!cameraControls.lastCameraViewData) {
      cameraControls.setLastCameraViewData(getCurrentViewData(camera.current));
    }

    const straightForwardQuaternion = new THREE.Quaternion();
    cameraControls.animateLookAtPosition(lookAtPosition, 1);

    await animateCamera(camera.current, cameraPosition, straightForwardQuaternion, 1);
  }
  async function onMouseDown(event) {
    mouseDown = true;
    if (!raycaster) return
    if (currentMode.current === DEFAULT) {
      // Sync the ray to the ACTUAL click position. `mouse` is otherwise only
      // refreshed on hover (onMouseMove is gated on !mouseDown), so right after
      // an orbit/rotate the cached NDC is stale and the click raycast misses —
      // the apartment doesn't get selected. Refresh from this event first.
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      raycaster.setFromCamera(mouse, camera.current);
      const intersects = raycaster.intersectObjects(scene.current.children, true);
      // console.log("intersects", intersects);
      const meshes = getAllMeshesFromIntersections(intersects);
      // console.log("meshes", meshes);

      const sprites = Object.values(modelsRef.current).filter(model => model.label).map((model => model.label));
      const spriteIntersects = raycaster.intersectObjects(sprites, false);

      if (spriteIntersects.length > 0) {
        const clickedSprite = spriteIntersects[0].object;
        // console.log("Clicked sprite:", clickedSprite.name);
        // console.log(modelsRef.current);
        await handleSpriteClick(clickedSprite);
        return;
      }
      if (!meshes || meshes.length == 0) return;
      const apartmentNode = meshes.find((node) => node.object.name.includes('bbox'));
      const areaNode = meshes.find((node) => node.object.name.includes('area'));

      if (apartmentNode && apartmentNode.object.userData.apartment) {
        onApartmentClick(apartmentNode.object.userData.apartment);
        return
      }
      else if (areaNode) {
        // boundaryNode.current = intersects[0].object;
        let heightPoint = intersects[0].point;
        // let heightPoint = getHighestPointFromIntersections(intersects);
        if (heightPoint) {
          // console.log("heightPoint", heightPoint);
          // Move the camera to the clicked point
          currentMode.current = APARTMENT;
          heightPoint.y = heightPoint.y + 1;
          // console.log("heightPoint", heightPoint);
          let strightForwardLoogAtPosition = calculateDestinationPoint(
            {
              position: heightPoint,
              quaternion: camera.current.quaternion,
            },
            100
          );
          strightForwardLoogAtPosition.y = heightPoint.y;
          cameraControls.setLastCameraViewData(getCurrentViewData(camera.current));
          const straightForwardQuaterion = new THREE.Quaternion();
          cameraControls.animateLookAtPosition(strightForwardLoogAtPosition, 2);
          await animateCamera(camera.current, heightPoint, straightForwardQuaterion, 2);
        }
      }
    }
  }
  const setInputHandlers = () => {
    renderer.current.domElement.addEventListener(
      "mousemove",
      onMouseMove,
      false
    );
    renderer.current.domElement.addEventListener(
      "mousedown",
      onMouseDown,
      false
    );
    renderer.current.domElement.addEventListener(
      "mouseup",
      function (event) {
        mouseDown = false;
      },
      false
    );
  };


  const moveToApartmentNode = async (apartmentNode) => {
    console.log("[FloorView] moveToApartmentNode position", apartmentNode && apartmentNode.position);
    if (!apartmentNode || !apartmentNode.children.length) {
      console.error(
        "[FloorView] moveToApartmentNode: apartment type model has no children — the GLB node for this apartment type is empty",
        { apartmentTypeNode: apartmentNode && apartmentNode.name }
      );
      return;
    }

    const targetAreaNodeName = "balcony"
    const targetArea = apartmentNode.children.find((n) =>
      n.name.includes(targetAreaNodeName)
    );

    if (!targetArea) {
      console.error(
        `[FloorView] moveToApartmentNode: no "${targetAreaNodeName}" node found under apartment type model — check the GLB node naming for this apartment type`,
        { apartmentTypeNode: apartmentNode.name, childNames: apartmentNode.children.map((n) => n.name) }
      );
      return;
    }
    console.log(targetAreaNodeName, targetArea.position);
    const targetNode = targetArea.children.find((n) =>
      n.name.includes("target")
    );

    const cameraMovmentAnimation = 0.5;

    // console.log("targetNode", targetNode);
    if (targetNode) {
      // boundaryNode.current = targetArea;
      targetNode.visible = false;
      const worldPosition = targetNode.localToWorld(
        new THREE.Vector3(0, 0, 0)
      );
      console.log("worldPosition", worldPosition);
      cameraControls.animateLookAtPosition(worldPosition, cameraMovmentAnimation);

    } else {
      console.error(
        `[FloorView] moveToApartmentNode: no "target" node found under "${targetAreaNodeName}" — check the GLB node naming for this apartment type`,
        { apartmentTypeNode: apartmentNode.name, balconyChildNames: targetArea.children.map((n) => n.name) }
      );
    }

    const boundingBox = new THREE.Box3().setFromObject(targetArea);
    const center = boundingBox.getCenter(new THREE.Vector3());

    // add human height
    center.y += 2;
    const straightForwardQuaterion = new THREE.Quaternion();

    await animateCamera(camera.current, center, straightForwardQuaterion, cameraMovmentAnimation);
    console.log("[FloorView] moveToApartmentNode: camera animation complete");
  };

  // const handleSelectedPosition = (positionName) => {
  //   // console.log("handleSelectedPosition", position);
  //   const node = selectedApartmentNode.children.find((n) =>
  //     n.name.includes(positionName)
  //   );
  //   const target = node.children.find((n) =>
  //     n.name.includes("target")
  //   );
  //   cameraControls.moveToNode(node);
  //   if (target) {
  //     target.visible = true;
  //     cameraControls.setControlsTarget(target.position);
  //   }
  // };
  const moveToDefaultView = () => {
    const targetPosition = projectDefaultView.default_view.position;
    animateCameraPosition(camera.current, targetPosition, 2);
  };
  const toggleAreasVisibility = (visible) => {
    if (scene.current) {
      areaNodesRef.current.forEach((node) => {
        node.visible = visible;
      });
    }
  };
  function checkIntersections(string) {
    // console.log("modelNodes",modelNodes)
    // Update the picking ray with the camera and mouse position
    raycaster.setFromCamera(mouse, camera.current);
    // console.log("scene.current.children",scene.current.children)
    // Calculate objects intersecting the picking ray
    const intersects = raycaster.intersectObjects(scene.current.children, true);
    // If intersects is not empty, intersects[0] is the closest intersected object
    if (intersects && intersects.length > 0) {
      // console.log(intersects)

      // strings.forEach((string) => {
      //   if (intersects[0].object.name.includes(string)) {

      const node = intersects.find((node) => node.object.name.includes(string));
      // const floorNode = intersects.find((node) =>
      //   node.object.name.includes("bbox")
      // );
      return node;
    } else {
      return null;
    }
  }
  function onMouseMove(event) {
    if (!mouse) return;
    // console.log(currentMode.current);
    if (!mouseDown && currentMode.current === DEFAULT) {
      const deltaX = mousePosition.current
        ? event.clientX - mousePosition.current.x
        : 0;
      const deltaY = mousePosition.current
        ? event.clientY - mousePosition.current.y
        : 0;
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      mousePosition.current = {
        x: event.clientX,
        y: event.clientY,
      };

      const apartmetNode = checkIntersections("bbox");
      const areaNode = checkIntersections("area");
      // console.log({areaNode})
      if (apartmetNode && apartmetNode.object.userData.apartment) {
        // console.log(apartmetNode.object.userData.apartment.id)
        apartmentManager.setAllApartmentsNodes([apartmetNode.object.userData.apartment.id]);
        onApartmentHover(
          apartmetNode.object.userData.apartment,
          mousePosition.current
        );
        document.getElementById("map").style.cursor = "pointer";
      }
      else if (areaNode) {
        areaNode.object.visible = true;
        document.getElementById("map").style.cursor = "pointer";
      }
      else {
        // console.log(appRef.current)
        apartmentManager.setAllApartmentsNodes();
        onApartmentHover();
        toggleAreasVisibility(false);
        document.getElementById("map").style.cursor = "default";
      }
      setMousePosition(mousePosition.current);
    }

  }

  const saveProject = (form) => {
    console.log(form);

    let projectSettings = {};
    Object.keys(form.Project).forEach((key) => {
      projectSettings[form.Project[key].property] = form.Project[key].value;
    });
    Object.keys(form.Floors).forEach((key) => {
      projectSettings[form.Floors[key].property] = form.Floors[key].value;
    });
    const newSettings = {
      ...projectDefaultView,
      ...projectSettings,
    };
    console.log(newSettings);
    saveProjectSettings(newSettings);
  };
  const saveViewData = (type = "app", data) => {
    console.log("saving viewData", type, data);
    const viewData = getCurrentViewData(camera.current);
    let modelToUpdate;
    switch (type) {
      case "app":
        saveApartmentViewData({
          ...viewedApartment.default_view,
          ...viewData,
        });
        break;
      case "app_view":
        saveApartmentViewData(data);
        break;
      case "model_view":
        saveApartmentViewData(data);
        break;
      case "model":
        modelToUpdate = projectModels.find(
          (b) => b.id == selectedModelId
        );
        saveModelCalibration(
          {
            ...modelToUpdate.default_view,
            camera: viewData,
            position: modelsRef.current[selectedModelId].position,
            rotation: modelsRef.current[selectedModelId].rotation,
            scale: modelsRef.current[selectedModelId].scale,
            // circleRadiusOffset:
            //   modelsRef.current[selectedModelId].circle.radiusOffset,
            // floorsLinesRotationY:
            //   modelsRef.current[selectedModelId].floorsGroup &&
            //     modelsRef.current[selectedModelId].floorsGroup.rotation.y
            //     ? modelsRef.current[
            //       selectedModelId
            //     ].floorsGroup.rotation.y.toFixed(2)
            //     : 0,
          },
          modelToUpdate.id,
          type
        );
        break;
      case "building":
        modelToUpdate = buildings.find(
          (b) => b.id == selectedModelId
        );
        saveModelCalibration(
          {
            ...modelToUpdate.default_view,
            camera: viewData,
            position: modelsRef.current[selectedModelId].position,
            rotation: modelsRef.current[selectedModelId].rotation,
            scale: modelsRef.current[selectedModelId].scale,
            circleRadiusOffset:
              modelsRef.current[selectedModelId].circle.radiusOffset,
            floorsLinesRotationY:
              modelsRef.current[selectedModelId].floorsGroup &&
                modelsRef.current[selectedModelId].floorsGroup.rotation.y
                ? modelsRef.current[
                  selectedModelId
                ].floorsGroup.rotation.y.toFixed(2)
                : 0,
          },
          modelToUpdate.id,
          type
        );
        break;
      case "building-floors":
        modelToUpdate = buildings.find(
          (b) => b.id == selectedModelId
        );
        saveModelCalibration(
          {
            ...modelToUpdate.default_view,
            floorsView: viewData,
          },
          modelToUpdate.id,
          'building'
        );
        break;
      case "project":
        const newSettings = {
          ...projectDefaultView,
          default_view: viewData,
        };
        saveProjectSettings(newSettings);
        break;

      default:
        break;
    }
  };
  const resetViewData = (type = "app",) => {
    console.log("resetting viewData", type);
    let modelToUpdate;
    switch (type) {
      case "app":
        saveApartmentViewData({});
        break;
      case "app_view":
        saveApartmentViewData({});
        break;
      case "model_view":
        saveApartmentViewData({});
        break;
      case "model":
        modelToUpdate = projectModels.find(
          (b) => b.id == selectedModelId
        );
        saveModelCalibration(
          {},
          modelToUpdate.id,
          type
        );
        break;
      case "building":
        modelToUpdate = buildings.find(
          (b) => b.id == selectedModelId
        );
        saveModelCalibration(
          {},
          modelToUpdate.id,
          type
        );
        break;

      case "project":

        saveProjectSettings({});
        break;

      default:
        break;
    }
  };

  const handleModelRotation = (rotationY) => {
    modelsRef.current[selectedModelId].rotation.y = rotationY / 10;
  };
  const handleFloorsRotation = (rotationY) => {
    modelsRef.current[selectedModelId].floorsGroup.rotation.y =
      rotationY / 10;
  };
  const handleModelScale = (scale) => {
    console.log(selectedModelId);
    console.log(modelsRef.current);
    modelsRef.current[selectedModelId].scale.set(scale, scale, scale);
    if (modelsRef.current[selectedModelId].floorsGroup)
      modelsRef.current[selectedModelId].floorsGroup.scale.set(
        scale,
        scale,
        scale
      );
  };
  const handleArrowsDistance = (distance) => {
    // console.log(arrowsGroup.current);
    // arrowsRadiusOffset.current = distance;
    const arrows = arrowsGroup.current.children;
    arrows.forEach((arrow) => {
      if (arrow.position.x > 0)
        arrow.position.x = initialArrowsRadius + +distance;
      if (arrow.position.x < 0)
        arrow.position.x = -initialArrowsRadius - +distance;
      if (arrow.position.z > 0)
        arrow.position.z = initialArrowsRadius + +distance;
      if (arrow.position.z < 0)
        arrow.position.z = -initialArrowsRadius - +distance;
    });
  };
  const handleArrowsRotation = (rotation) => {
    // console.log(arrowsGroup.current);
    // arrowsRadiusOffset.current = distance;
    arrowsGroup.current.rotation.y = rotation / 10;

  };

  const handleFloorsLinesColor = (color) => {
    floorLines.current.forEach((line) => {
      line.material.color.set(color);
    });
  };

  const updateLine = (line, start, end) => {
    // console.log({start, end})
    // Update the points in the geometry
    line.geometry.setFromPoints([start, end]);

    // Need to tell Three.js to update the line
    line.geometry.verticesNeedUpdate = true;
  };
  const updateLabelPosition = (label, position) => {
    label.position.set(position.x, position.y, position.z);
  };

  const updateLinesStart = (value) => {
    floorLineStartXOffset.current = value;
    floorLines.current.forEach((line) => {
      let startPos = { ...line.userData.startPos };
      let endPos = { ...startPos };

      if (line.userData.startPos.x > 0) {
        startPos.x = startPos.x + value;
        endPos.x = startPos.x + floorLinesWidth.current;
      } else {
        startPos.x = startPos.x - value;
        endPos.x = startPos.x - +floorLinesWidth.current;
      }
      updateLine(line, startPos, endPos);
      updateLabelPosition(line.label, endPos);
    });
  };
  const updatelinesWidth = (value) => {
    // console.log(floorLines.current);
    // console.log(floorLabels.current)
    floorLinesWidth.current = +value;
    floorLines.current.forEach((line) => {
      let startPos = { ...line.userData.startPos };
      let endPos = { ...startPos };
      if (line.userData.startPos.x > 0) {
        startPos.x += floorLineStartXOffset.current;
        endPos.x = startPos.x + +value;
      } else {
        startPos.x -= floorLineStartXOffset.current;
        endPos.x = startPos.x - +value;
      }
      updateLine(line, startPos, endPos);
      updateLabelPosition(line.label, endPos);
    });
  };
  const handleInputChange = (e, tab) => {
    const { label, value } = e;
    switch (label) {
      case "X":
        modelsRef.current[selectedModelId].position.x = +value;
        break;
      case "Y":
        modelsRef.current[selectedModelId].position.y = +value;
        break;
      case "Z":
        modelsRef.current[selectedModelId].position.z = +value;
        break;
      case ARROWS_DISTANCE:
        handleArrowsDistance(+value);

        break;
      case ARROWS_ROTATION:
        handleArrowsRotation(+value);

        break;
      // case PODIUM_SCALE:
      //   changePodiumSize(+value);

      //   break;
      case "Circle size":
        if (tab == "Project") handleCircleSize(+value, mainCircle.current);
        else
          handleCircleSize(
            +value,
            modelsRef.current[selectedModelId].circle
          );
        break;
      case "Resize model":
        handleModelScale(+value);
        break;
      case ROTATE_MODEL:
        handleModelRotation(+value);
        break;
      case ROTATE_FLOORS:
        modelsRef.current[selectedModelId].floorsGroup &&
          handleFloorsRotation(+value);
        break;
      case "Line width":
        updatelinesWidth(+value);
        break;
      case "Line start":
        updateLinesStart(+value);

        break;
      case "Line color":
        console.log(floorLines.current);

        handleFloorsLinesColor(value);
        break;
      case "Max distance":
        controls.current.maxDistance = +value;
        break;
      case TRANSPARENCY:
        console.log("TRANSPARENCY", value);
        vacantColor.opacity = value;
        occupiedColor.opacity = value;
        break;
      case ROTATE_MODEL + "X":
        // tilesRenderer.current.group.rotation.x = +value ;
        break;
      default:
        break;
    }
  };
  let style = {
    position: "absolute",
    top: 0,
    left: !isRtl ? "auto" : '3rem',
    right: !isRtl ? '3rem' : "auto",
    backgroundColor: "lightgrey",
    color: "black",
  };
  const handleEndInitialAnimation = useCallback(() => {
    console.log("handleEndInitialAnimation");
    setAnimationEnded((pre) => !pre);
    controls.current = new OrbitControls(
      camera.current,
      renderer.current.domElement
    );
    cameraControls.setControls();
    cameraControls.enableControls();
    onAnimationEnd();
    setEnvironmentPreset("Sunset")
    setTimeout(() => {
      setAnimationEnded((pre) => !pre);
    }, 20);
  });
  const handleBackButtonClick = useCallback(() => {
    console.log("handleBackButtonClick", focusedBuilding, currentMode.current);
    if (focusedBuilding && currentMode.current === APARTMENT) {
      console.log("Returning to building view:");
      // onBackButtonClick();
      // setTimeout(() => {
        focuseToBuildingByName(focusedBuilding);
      // }, 200);
      
      

    } 
    else {

      toggleModelsVisibility(Object.keys(modelsRef.current));
      setShowGrid(false);
      setShowEnvironment(true);
      setFocusedBuilding(null);
      cameraControls.enableControls();
      currentMode.current = DEFAULT
      cameraControls.setLastCameraViewData(null);
      setSelectedFloor(null);
      onBackButtonClick();

      if (cameraControls.lastCameraViewData)
        cameraControls.moveCameraToLastView();
      else moveToDefaultView();

    }
  }, [cameraControls.moveCameraToLastView, onBackButtonClick, focusedBuilding, currentMode.current]);
  
  const showBackButton = cameraControls.lastCameraViewData ? true : false
  // const showBackButton = true
  console.log("Scene render", currentMode.current, touredApartment);
  return (
    <>
      <div ref={mountRef} />
      {initialized && (
        <>
          <CameraController
            camera={camera.current}
            controls={controls.current}
            projectDefaultView={projectDefaultView}
            onAnimationEnd={handleEndInitialAnimation}
            modelsLoaded={modelsLoaded}
            animationEnded={animationEnded}
            cameraPosition={cameraPosition}
          // position
          />
          <Presets
            animationEnded={animationEnded}
            showPresets={showPresets}
            projectImages={projectImages}
            isRtl={isRtl}
            setPreset={setEnvironmentPreset}
            environmentPreset={environmentPreset}
            showBackButton={showBackButton}
            onBackButtonClick={handleBackButtonClick}
            show360={touredApartment ? true : false}
            showGrid={showGrid}
            setShowGrid={() => setShowGrid((prev) => !prev)}
          />
          <EnvironmentManager
            scene={scene.current}
            renderer={renderer.current}
            animationEnded={animationEnded}
            selectedFloor={selectedFloor}
            floors={floors}
            projectImages={projectImages}
            environmentPreset={environmentPreset}
            modelsRef={modelsRef.current}
            setSelectedFloor={setSelectedFloor}
            isRtl={isRtl}
            showEnvironment={showEnvironment}
          />

          {/* <ModelsManager
            scene={scene.current}
            parent={selectedApartmentNode}
            setModelsLoaded={setModelsLoaded}
          /> */}
          <LightsManager scene={scene.current} parent={selectedApartmentNode} />

          {projectImages.length < 1 && <SkyController
            setEnvironmentPreset={setEnvironmentPreset}
            environmentPreset={environmentPreset}
            selectedFloor={selectedFloor}
            showOcean={projectDefaultView.showOcean}
            // gridSize={gridSize}
            scene={scene.current}
            renderer={renderer.current}
            camera={camera.current}
            animationEnded={animationEnded}
            modelsLoaded={modelsLoaded}
            isRtl={isRtl}
            showEnvironment={showEnvironment}
          />}
          <Grid
            scene={scene.current}
            gridSize={gridSize}
            show={showGrid && animationEnded}
          />
          {tileSetRef && <TileSet
            ref={tileSetRef}
            url={tileSetUrl}
            scene={scene.current}
            renderer={renderer.current}
            camera={camera.current}
            // rotation={{ x: -Math.PI / 2, y: 0, z: 0 }}
            // onLoadTileSet={(tileset) => console.log('Tileset loaded from ThreeScene:', tileset)}
            onError={(error) => console.error('Tileset error from ThreeScene:', error)}
          />}
          <TexturesManager
            selectedApartmentNode={selectedApartmentNode}
            scene={scene.current}
          /> 
          {touredApartment && <MovementControls colors={colors} isRtl={isRtl} />}
          {touredApartment && <MobileMovementController />}

          {/* Positions Slider - shown when there are building positions available */}
          {buildingPositions.length > 0 && animationEnded && currentMode.current === DEFAULT && (
            <PositionsSlider
              positions={buildingPositions}
              camera={camera}
              controls={controls}
              direction="vertical"
              isRtl={isRtl}
              colors={colors}
              // onPositionChange={(index, position) => {
              //   console.log(`Slider position ${index}:`, position);
              // }}
              currentBuildingName={focusedBuilding}
              setCurrentBuildingName={(buildingName) => {
                const building = buildings.find(b => b.name === buildingName);
                if (!building) return;
                onBuildingSelect(building.id);
                if (focusedBuilding ) {
                  toggleModelsVisibility(building.id);
                }
              }}
              style={{ left: 4, top: 60 }}
            />
          )}

          {/* Compass showing N/S direction */}
          {animationEnded && (
            <Compass
              controls={controls.current}
              arrowsRotation={projectDefaultView?.arrowsRotation}
              colors={colors}
            />
          )}

        </>
      )}

      {isAdmin && (
        <div className="settings" style={style}>
          {viewedApartment ? (
            <ApartmentSettings
              apartment={viewedApartment}
              saveViewData={saveViewData}
              camera={camera.current}
            />
          ) : (
            <Settings
              app={viewedApartment}
              buildings={buildings}
              projectModels={projectModels}
              projectDefaultView={projectDefaultView}
              // settingsForm={settingsForm}
              saveStatus={saveStatus}
              saveProjectSettings={saveProjectSettings}
              onChange={handleInputChange}
              saveProject={(form) => saveProject(form)}
              saveViewData={saveViewData}
              resetViewData={resetViewData}
              selectedModelId={selectedModelId}
              setSelectedModelId={setSelectedModelId}
            />
          )}
        </div>
      )}
    </>
  );
});

export default Scene;
